import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 黄瓜视频" description="抱歉，您查找的文章不存在或已被移除。" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="w-full px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-search-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的文章不存在或已被移除。</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter(a => a.id !== article.id).slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '黄瓜视频',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 黄瓜视频在线观看`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},黄瓜视频,在线观看`}
        canonicalUrl={`https://huanggua.video/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        jsonLd={articleJsonLd}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '文章', href: '/about' },
              { label: article.title },
            ]} />

            <div className="mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 黄瓜视频在线观看`}
                className="w-full rounded-lg aspect-[16/9] object-cover"
              />
            </div>

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">{article.title}</h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-accent-100 text-accent-700 whitespace-nowrap">
                {article.categoryName}
              </span>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <h2 className="text-lg font-heading text-foreground-900 mb-5">相关文章</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} />
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}